import { useRef, useCallback, useState, type ReactNode } from "react";
import { useWidgets } from "../context/WidgetContext";
import { artifacts } from "../api/client";

interface FloatingWidgetProps {
  id: string;
  title: string;
  widgetType?: string;
  data?: Record<string, unknown>;
  position: { x: number; y: number };
  size: { w: number; h: number };
  minimized: boolean;
  zIndex: number;
  children?: ReactNode;
}

export function FloatingWidget({
  id,
  title,
  widgetType,
  data,
  position,
  size,
  minimized,
  zIndex,
  children,
}: FloatingWidgetProps) {
  const { dispatch } = useWidgets();
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);
  const [showSave, setShowSave] = useState(false);
  const [saveForm, setSaveForm] = useState({ title, description: "", category: "" });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const canSave = widgetType === "html" && !!data?.html;

  async function handleSave() {
    if (saving || !data?.html) return;
    setSaving(true);
    try {
      await artifacts.save({
        title: saveForm.title,
        description: saveForm.description,
        category: saveForm.category || "uncategorized",
        widgetType: widgetType ?? "html",
        html: data.html as string,
        size,
      });
      setSaved(true);
      setShowSave(false);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      // silently fail — user can retry
    }
    setSaving(false);
  }

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      dispatch({ type: "FOCUS", widgetId: id });
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        origX: position.x,
        origY: position.y,
      };

      const onPointerMove = (ev: PointerEvent) => {
        if (!dragRef.current) return;
        const dx = ev.clientX - dragRef.current.startX;
        const dy = ev.clientY - dragRef.current.startY;
        dispatch({
          type: "MOVE",
          widgetId: id,
          position: {
            x: Math.max(0, dragRef.current.origX + dx),
            y: Math.max(0, dragRef.current.origY + dy),
          },
        });
      };

      const onPointerUp = () => {
        dragRef.current = null;
        document.removeEventListener("pointermove", onPointerMove);
        document.removeEventListener("pointerup", onPointerUp);
      };

      document.addEventListener("pointermove", onPointerMove);
      document.addEventListener("pointerup", onPointerUp);
    },
    [id, position, dispatch],
  );

  if (minimized) {
    return (
      <div
        className="fixed bottom-0 bg-gray-800 border border-gray-700 rounded-t-lg px-3 py-1.5 cursor-pointer hover:bg-gray-700 flex items-center gap-2 text-sm text-gray-300"
        style={{ zIndex }}
        onClick={() => dispatch({ type: "RESTORE", widgetId: id })}
      >
        <span className="truncate max-w-[150px]">{title}</span>
      </div>
    );
  }

  return (
    <div
      className="absolute bg-gray-900 border border-gray-700 rounded-lg shadow-2xl flex flex-col overflow-hidden"
      style={{
        left: position.x,
        top: position.y,
        width: size.w,
        height: size.h,
        zIndex,
      }}
      onPointerDown={() => dispatch({ type: "FOCUS", widgetId: id })}
    >
      {/* Title bar — drag handle */}
      <div
        className="flex items-center justify-between px-3 py-2 bg-gray-800 border-b border-gray-700 cursor-grab active:cursor-grabbing select-none shrink-0"
        onPointerDown={onPointerDown}
      >
        <span className="text-sm font-medium text-gray-200 truncate">{title}</span>
        <div className="flex items-center gap-1 ml-2">
          {canSave && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (saved) return;
                setShowSave(!showSave);
                setSaveForm((f) => ({ ...f, title }));
              }}
              className={`w-6 h-6 flex items-center justify-center rounded text-xs ${
                saved
                  ? "text-green-400"
                  : "text-gray-400 hover:text-blue-400 hover:bg-gray-700"
              }`}
              title={saved ? "Saved" : "Save artifact"}
            >
              {saved ? "\u2713" : "\u2661"}
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              dispatch({ type: "MINIMIZE", widgetId: id });
            }}
            className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-200 hover:bg-gray-700 rounded text-xs"
            title="Minimize"
          >
            _
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              dispatch({ type: "CLOSE", widgetId: id });
            }}
            className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded text-xs"
            title="Close"
          >
            x
          </button>
        </div>
      </div>

      {/* Save form dropdown */}
      {showSave && (
        <div
          className="bg-gray-850 border-b border-gray-700 px-3 py-2 space-y-2 shrink-0"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <input
            type="text"
            value={saveForm.title}
            onChange={(e) => setSaveForm((f) => ({ ...f, title: e.target.value }))}
            placeholder="Title"
            className="w-full px-2 py-1.5 bg-gray-800 border border-gray-700 rounded text-xs text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
          />
          <input
            type="text"
            value={saveForm.description}
            onChange={(e) => setSaveForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="Description (optional)"
            className="w-full px-2 py-1.5 bg-gray-800 border border-gray-700 rounded text-xs text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
          />
          <input
            type="text"
            value={saveForm.category}
            onChange={(e) => setSaveForm((f) => ({ ...f, category: e.target.value }))}
            placeholder="Category (e.g. tools, games, dashboards)"
            className="w-full px-2 py-1.5 bg-gray-800 border border-gray-700 rounded text-xs text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
          />
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setShowSave(false)}
              className="px-3 py-1 text-xs text-gray-400 hover:text-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !saveForm.title.trim()}
              className="px-3 py-1 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-700 disabled:text-gray-500 text-white text-xs rounded"
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-auto p-3 text-sm text-gray-300">
        {children}
      </div>
    </div>
  );
}
